import { Autosuggest, AutosuggestProps } from '@amzn/awsui-components-react-v3';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { activityGroupSlice } from '../../../../../store/slices/activityGroupSlice';
import { AciResouceConstrain } from '../../utils/validations';
import { InstructorProfileData } from '../../../../../../common/interfaces/instructorProfile';
import instructorManagementApi from '../../../../../../imt/api/instructorManagementApi';
import { DeliveryInstructor } from '../../../../../interfaces/activity';

interface InstructorAttributeEditorProps {
    instructor: DeliveryInstructor;
    deliveryName: string;
    optionNumber: number;
    sessionId: string;
    isReadOnlyMode: boolean;
    instructorPool: DeliveryInstructor[];
}

const InstructorAttributeEditor = ({
    instructor,
    deliveryName,
    optionNumber,
    sessionId,
    isReadOnlyMode,
    instructorPool,
}: InstructorAttributeEditorProps) => {
    const dispatch = useDispatch();

    const [searchResultOptions, setSearchResultOptions] = useState<
        AutosuggestProps.Option[]
    >([]);
    const [isSearchingInstructors, setIsSearchingInstructors] =
        useState<boolean>(false);
    const [instructorSearchText, setInstructorSearchText] = useState('');

    const displayValue =
        instructorSearchText ||
        (instructor && instructor.name
            ? `${instructor.name} (${instructor.email})`
            : '');

    const instructorPoolOptions: AutosuggestProps.Option[] = instructorPool.map(
        ({ pk, name, email, type, location }) => ({
            value: JSON.stringify({
                pk,
                name,
                email,
                type,
                location,
            }),
            label: `${name} (${email})`,
        }),
    );

    const combinedOptions: {
        label: string;
        options: any;
    }[] = [
        {
            label: 'Saved results',
            options: instructorPoolOptions,
        },
        ...(searchResultOptions.length > 0
            ? [
                  {
                      label: 'Search result',
                      options: searchResultOptions,
                  },
              ]
            : []),
    ];

    return (
        <Autosuggest
            data-testid="instructor-search"
            ariaDescribedby=""
            ariaLabel="Search input"
            ariaLabelledby=""
            clearAriaLabel="Clear"
            controlId="autosuggest-1"
            errorIconAriaLabel="Error"
            errorText="Error loading results"
            selectedAriaLabel="Selected"
            enteredTextLabel={(value) => `Use: "${value}"`}
            value={displayValue}
            placeholder="Search for an instructor"
            loadingText="Searching..."
            empty="No instructors found"
            recoveryText="Retry search"
            finishedText={
                searchResultOptions.length > 0 && displayValue.length > 0
                    ? `Results for: ${displayValue}`
                    : ''
            }
            statusType={isSearchingInstructors ? 'loading' : 'finished'}
            filteringType="manual"
            disabled={isReadOnlyMode}
            onChange={(e) => {
                let clickedOption = null;

                for (const group of combinedOptions) {
                    const foundOption = group.options.find(
                        (option) =>
                            option.value === e.detail.value ||
                            option.label === e.detail.value,
                    );

                    if (foundOption) {
                        clickedOption = foundOption;
                        break;
                    }
                }

                if (clickedOption) {
                    const selectedInstructor = JSON.parse(clickedOption.value);

                    dispatch(
                        activityGroupSlice.actions.addInstructorToPool(
                            selectedInstructor,
                        ),
                    );
                    dispatch(
                        activityGroupSlice.actions.removeInstructorFromDeliverySession(
                            {
                                deliveryName,
                                optionNumber,
                                instructorPk: instructor.pk,
                                deliverySessionId: sessionId,
                            },
                        ),
                    );

                    dispatch(
                        activityGroupSlice.actions.addInstructorToDeliverySession(
                            {
                                deliveryName,
                                optionNumber,
                                instructor: {
                                    pk: selectedInstructor.pk,
                                    name: selectedInstructor.name,
                                    type: selectedInstructor.type,
                                    location: selectedInstructor.location,
                                    role: instructor.role,
                                    do_not_shuffle: false,
                                    email: selectedInstructor.email,
                                },
                                deliverySessionId: sessionId,
                            },
                        ),
                    );

                    setInstructorSearchText('');
                    setSearchResultOptions([]);
                } else {
                    setInstructorSearchText(e.detail.value);
                }
            }}
            onLoadItems={async (e) => {
                if (e.detail.filteringText) {
                    setIsSearchingInstructors(true);

                    try {
                        const { result } =
                            await instructorManagementApi.getInstructors({
                                search_text: e.detail.filteringText,
                                from: 0,
                                size: AciResouceConstrain.MAX_INSTRUCTOR_SEARCH_RESULTS,
                            });

                        setSearchResultOptions(
                            result.instructors.map(
                                (instructorData: InstructorProfileData) => ({
                                    value: JSON.stringify({
                                        pk: instructorData.pk,
                                        name: instructorData.full_name,
                                        email: instructorData.email,
                                        type: instructorData.instructor_type,
                                        location: instructorData.city,
                                        role: instructor.role,
                                        do_not_shuffle: false,
                                    }),
                                    label: `${instructorData.full_name} (${instructorData.email})`,
                                }),
                            ),
                        );
                    } catch (error) {
                        setSearchResultOptions([]);
                    } finally {
                        setIsSearchingInstructors(false);
                    }
                }
            }}
            options={combinedOptions}
        />
    );
};

export default InstructorAttributeEditor;
